<template>
    <div
        v-if="!$isMobile"
        id="print-quiz"
        class="modal-background-color z-index-102 quick-quiz-preview-root"
        :class="{ 'quick-quiz-preview-root--tablet': $isTablet }"
    >
        <div style="width: auto; z-index: 102 !important" class="modal-content quick-quiz-preview-root__body">
            <header class="modal-card-head quick-quiz-preview-root__head p-t-lg">
                <div v-if="!isLoading" class="modal-card-title quick-quiz-preview-root__title">
                    <span class="quick-quiz-preview-root__title-text">{{ previewTitle }}</span>
                    <e-taglist attached class="quick-quiz-preview-root__tags">
                        <e-tag class="has-text-weight-bold">#{{ quizDetails.original_version_id }}</e-tag>
                        <e-tag type="is-primary" class="has-text-weight-bold">v{{ quizDetails.version_number }}</e-tag>
                    </e-taglist>
                </div>
                <button type="button" class="delete quick-quiz-preview-root__close" @click="close" />
            </header>

            <section>
                <e-field v-if="activity" class="columns" style="padding: 0; margin: 0 !important; max-height: 22rem">
                    <ul class="quiz-activity-settings m-r-md column">
                        <li>
                            <span>
                                <strong class="setting-titles"> Allow Submission After Due Date: </strong>
                                {{ activity.activity_settings.allow_submissions_after_due_date ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Downloadable:</strong>
                                {{ activity.activity_settings.is_downloadable ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Extra Credit: </strong>
                                {{ activity.activity_settings.is_extra_credit ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Max Attempts: </strong>
                                {{ activity.activity_settings.max_attempts_allowed }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Password Enabled: </strong>
                                {{ activity.activity_content.password_enabled ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Answer in Order: </strong>
                                {{ quizDetails.must_answer_in_order ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Calculator Enabled: </strong>
                                {{ quizDetails.calculator_enabled ? 'Yes' : 'No' }}
                            </span>
                        </li>
                    </ul>

                    <ul class="quiz-activity-settings m-r-md column">
                        <li>
                            <span>
                                <strong class="setting-titles"> Review Allowed: </strong>
                                {{ activity.activity_content.review_allowed ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Show Answers in Review: </strong>
                                {{ activity.activity_content.show_answers_in_review ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Show Correct Answers in Review: </strong>
                                {{ activity.activity_content.show_correct_answers_in_review ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Show Scores in Review: </strong>
                                {{ activity.activity_content.show_score_in_review ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Auto Grade: </strong>
                                {{ activity.activity_settings.auto_grade ? 'Yes' : 'No' }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Randomize Question Order: </strong>
                                {{ quizDetails.random_question_order ? 'Yes' : 'No' }}
                            </span>
                        </li>
                    </ul>
                    <ul class="quiz-activity-settings column">
                        <li v-if="activity.activity_content.time_limit !== null">
                            <span>
                                <strong class="setting-titles"> Time Limit: </strong>
                                {{ activity.activity_content.time_limit / 60 }}

                                {{ pluralize('minute', activity.activity_content.time_limit / 60) }}
                            </span>
                        </li>

                        <li v-else>
                            <span><strong class="setting-titles"> No Time Limit </strong></span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Total Questions:</strong> {{ questions.length }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Navigation Locked: </strong>
                                {{ activity.activity_content.lockdown ? `Yes` : `No` }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Managed Quiz :</strong>
                                {{ activity.activity_content.force_start ? `Yes` : `No` }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Due at: </strong>
                                {{ formatDate(activity.activity_settings.due_at, 'MM/DD/YYYY hh:mm:ss zz') }}
                            </span>
                        </li>

                        <li>
                            <span>
                                <strong class="setting-titles"> Questions Per Page: </strong>
                                {{ quizDetails.questions_per_page }}
                            </span>
                        </li>
                    </ul>
                </e-field>

                <!-- Use a plain div (not e-field): EField uses inheritAttrs:false and drops classes, so .scrollable-container never applied. -->
                <div v-if="questions.length" class="m-t-none scrollable-container">
                    <div
                        class="modal-card-body print-style quick-quiz-preview-questions"
                        style="overflow-x: hidden; padding: 0"
                    >
                        <div
                            v-for="(question, qIndex) in questions"
                            :key="question.id"
                            class="quick-quiz-preview-question"
                            :class="{ 'quick-quiz-preview-question--striped': qIndex % 2 === 1 }"
                        >
                            <display-content
                                :content="`Question ${question.display_order + 1}: ${question.body}`"
                                class="display-content-custom m-b-md"
                            />
                            <span
                                v-for="answer in question.answers"
                                :key="answer.id"
                                style="display: flex"
                                class="m-b-md"
                            >
                                <e-radio
                                    :model-value="answer.is_correct"
                                    :native-value
                                    type="is-success"
                                    disabled
                                />
                                <display-content class="display-content-custom m-r-xs" :content="answer.body || ''" />
                                <p style="color: #28bdb0; align-self: center">
                                    {{ answer.is_correct ? '(Correct)' : null }}
                                </p>
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <e-loading v-if="isLoading" v-model:active="isLoading" is-full-page />

        <div id="preview-button" class="modal-card-foot tw:flex quick-quiz-preview-root__footer">
            <div class="tw:flex tw:justify-between tw:w-full tw:justify-end tw:gap-2">
                <e-button v-if="canPrint" @click="printPreview">
                    {{ type === 'quiz' ? 'Print Quiz' : 'Print Survey' }}
                </e-button>
                <e-button @click="close"> Close</e-button>
            </div>
        </div>
    </div>

    <div v-else class="mobile-modal quick-quiz-preview-mobile">
        <header class="modal-card-head quick-quiz-preview-mobile__head">
            <p v-if="!isLoading" class="modal-card-title quick-quiz-preview-mobile__title">{{ previewTitle }}</p>
            <button type="button" class="delete quick-quiz-preview-mobile__close" @click="close" />
        </header>

        <div class="quick-quiz-preview-mobile__body">
            <section>
                <e-collapse v-if="activity" class="card" animation="slide" aria-id="contentIdForA11y3" :open="false">
                    <template #trigger="{ open }">
                        <div
                            class="card-header m-b-xs"
                            role="button"
                            aria-controls="contentIdForA11y3"
                            :aria-expanded="open.toString()"
                            tabindex="0"
                            @click="isOpen = !isOpen"
                            @keydown.enter="isOpen = !isOpen"
                        >
                            <p class="card-header-title">
                                {{ isOpen ? `Collapse Settings` : `Open Settings` }}
                            </p>
                            <a class="card-header-icon" tabindex="-1">
                                <!-- tabindex="-1" makes it non-navigable via keyboard -->
                                <e-icon :icon="open ? 'angle-up' : 'angle-down'" />
                            </a>
                        </div>
                    </template>

                    <div class="card-content">
                        <div class="content">
                            <e-field>
                                <ul
                                    v-show="mobileSettingOption === `Admin` || mobileSettingOption === null"
                                    class="quiz-activity-settings m-l-xs"
                                >
                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Allow Submission After Due Date: </strong>
                                            {{
                                                activity.activity_settings.allow_submissions_after_due_date
                                                    ? 'Yes'
                                                    : 'No'
                                            }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Downloadable:</strong>
                                            {{ activity.activity_settings.is_downloadable ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Extra Credit: </strong>
                                            {{ activity.activity_settings.is_extra_credit ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Max Attempts: </strong>
                                            {{ activity.activity_settings.max_attempts_allowed }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Password Enabled: </strong>
                                            {{ activity.activity_content.password_enabled ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Auto Grade: </strong>
                                            {{ activity.activity_settings.auto_grade ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Questions Per Page: </strong>
                                            {{ quizDetails.questions_per_page }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Randomize Question Order: </strong>
                                            {{ quizDetails.random_question_order ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Answer in Order: </strong>
                                            {{ quizDetails.must_answer_in_order ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Calculator Enabled: </strong>
                                            {{ quizDetails.calculator_enabled ? 'Yes' : 'No' }}
                                        </span>
                                    </li>
                                </ul>

                                <ul v-show="mobileSettingOption === `Content`" class="quiz-activity-settings m-l-xs">
                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Review Allowed: </strong>
                                            {{ activity.activity_content.review_allowed ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Show Answers in Review: </strong>
                                            {{ activity.activity_content.show_answers_in_review ? 'Yes' : 'No' }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Show Correct Answers in Review: </strong>
                                            {{
                                                activity.activity_content.show_correct_answers_in_review ? 'Yes' : 'No'
                                            }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Show Scores in Review: </strong>
                                            {{ activity.activity_content.show_score_in_review ? 'Yes' : 'No' }}
                                        </span>
                                    </li>
                                </ul>

                                <ul v-show="mobileSettingOption === `General`" class="quiz-activity-settings m-l-xs">
                                    <li v-if="activity.activity_content.time_limit !== null">
                                        <span>
                                            <strong class="setting-titles"> Time Limit: </strong>
                                            {{ activity.activity_content.time_limit / 60 }}

                                            {{ pluralize('minute', activity.activity_content.time_limit / 60) }}
                                        </span>
                                    </li>

                                    <li v-else>
                                        <span><strong class="setting-titles"> No Time Limit </strong></span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Total Questions:</strong>
                                            {{ questions.length }}
                                        </span>
                                    </li>

                                    <li v-if="activity.activity_content.lockdown">
                                        <span><strong class="setting-titles"> Navigation Locked</strong></span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Managed Quiz :</strong>
                                            {{ activity.activity_content.force_start ? `Yes` : `No` }}
                                        </span>
                                    </li>

                                    <li>
                                        <span>
                                            <strong class="setting-titles"> Due at: </strong>
                                            {{
                                                formatDate(activity.activity_settings.due_at, 'MM/DD/YYYY hh:mm:ss zz')
                                            }}
                                        </span>
                                    </li>
                                </ul>
                            </e-field>
                        </div>
                    </div>

                    <footer class="card-footer">
                        <a class="card-footer-item" @click="mobileSettingOption = `Admin`">Admin</a>
                        <a class="card-footer-item" @click="mobileSettingOption = `Content`">Content</a>
                        <a class="card-footer-item" @click="mobileSettingOption = `General`">General </a>
                    </footer>
                </e-collapse>

                <section v-if="!isOpen" class="m-t-none">
                    <div class="modal-card-body quick-quiz-preview-questions">
                        <div
                            v-for="(question, qIndex) in questions"
                            :key="question.id"
                            class="quick-quiz-preview-question"
                            :class="{ 'quick-quiz-preview-question--striped': qIndex % 2 === 1 }"
                            style="text-align: left"
                        >
                            <display-content
                                style="
                                    max-width: 100% !important;
                                    overflow: unset;
                                    white-space: pre-wrap !important;
                                    text-overflow: clip !important;
                                "
                                :content="` Question ${question.display_order + 1} : ${question.body}`"
                            />

                            <span v-for="answer in question.answers" :key="answer.id">
                                <div style="width: 100%; display: flex; justify-content: flex-start">
                                    <e-radio :model-value="answer.is_correct" type="is-success" disabled>
                                        <display-content style="max-width: unset; overflow: unset" :content="answer.body" />
                                    </e-radio>

                                    <p style="color: #28bdb0; align-self: center">{{ answer.is_correct ? '(Correct)' : null }}</p>
                                </div>
                            </span>
                        </div>
                    </div>
                </section>
            </section>
        </div>

        <footer class="modal-card-foot quick-quiz-preview-mobile__foot">
            <e-button @click="close">Close</e-button>
        </footer>

        <e-loading v-if="isLoading" v-model:active="isLoading" is-full-page />
    </div>
</template>

<script>
import pluralize from 'pluralize';
import { mapState } from 'vuex';

import DisplayContent from '@/components/editor/DisplayContent.vue';
import axios from '@/plugins/axios.js';

export default {
    name: 'QuickQuizView',

    components: { DisplayContent },

    props: {
        id: {
            type: Number,
            required: true,
        },

        activity: {
            type: Object,
            required: false,
            default: null,
        },

        hasQuizAttempt: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    emits: ['close'],

    data: () => ({
        mobileSettingOption: null,
        questions: [],
        quizDetails: {},
        uuid: {},
        isOpen: false,
        isLoading: false,
        quiz: null,
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        title() {
            return this.quiz?.title;
        },

        previewTitle() {
            return this.title || this.quizDetails?.title || '';
        },

        canPrint() {
            return this.user.hasAnyRole(['Owner', 'Admin', 'Instruction Admin']);
        },

        type() {
            return this.quizDetails?.type || 'quiz';
        },
    },

    beforeUnmount() {
        this.quizDetails = null;
    },

    async created() {
        this.isLoading = true;

        this.quiz = await axios.get(`/api/quizzes/${this.id}`).then(resp => resp.data.data);

        // ternary operator in request determines if course-activity check permissions.
        this.questions = await axios
            .get(
                `/api/questions?filter[quiz_id]=${this.id}&show_correct=true&preview=true&course=${
                    this.activity ? this.activity.course_id : false
                }`
            )
            .then(resp => resp.data.data);

        // gets extra details/settings regarding the quiz
        this.quizDetails = await this.$store.dispatch('QuizBuilder/fetch', {
            route: 'quizzes',
            type: 'single',
            id: this.id,
        });

        this.isLoading = false;
    },

    methods: {
        pluralize,

        async printPreview() {
            const button = document.getElementById('preview-button');
            const table = document.getElementsByClassName('print-style');
            const quizSettingItems = document.getElementsByClassName('quiz-activity-settings');
            const modalContent = document.getElementsByClassName('modal-content');
            const modalOverflowEl = modalContent.length > 0 ? modalContent[0] : null;
            const displayContentElements = document.getElementsByClassName('display-content-custom');

            displayContentElements.forEach(displayContentElement => {
                if (quizSettingItems.length) {
                    displayContentElement.style.inlineSize = '50rem';
                    displayContentElement.style.overflowWrap = 'break-word';
                }
            });

            quizSettingItems.forEach(quizSettingItem => {
                if (quizSettingItems.length) {
                    quizSettingItem.style.paddingTop = '2rem';
                    quizSettingItem.style.paddingLeft = '2rem';
                }
            });

            if (table[0]) {
                table[0].style.maxHeight = 'none';
            }

            button.style.display = 'none';

            if (modalOverflowEl) {
                modalOverflowEl.style.overflow = 'visible';
            }

            this.$htmlToPaper('print-quiz');

            quizSettingItems.forEach(quizSettingItem => {
                if (quizSettingItems.length) {
                    quizSettingItem.style.paddingTop = '2rem';
                    quizSettingItem.style.paddingLeft = '2rem';
                }
            });

            displayContentElements.forEach(displayContentElement => {
                if (quizSettingItems.length) {
                    displayContentElement.style.inlineSize = 'unset';
                    displayContentElement.style.overflowWrap = 'unset';
                }
            });

            if (table[0]) {
                table[0].style.maxHeight = '';
            }

            button.style.display = 'flex';
            // Do not restore overflow:hidden — it clips the preview scroll area (see .modal-content inline style).
            if (modalOverflowEl) {
                modalOverflowEl.style.overflow = '';
            }
        },

        close() {
            if (this.hasQuizAttempt) {
                if (this.$route.name === 'quiz-view') {
                    this.$router.push({ name: 'quizzes-index' });
                }
                if (this.$route.name === 'course-edit') {
                    window.location.reload();
                }

                if (this.$route.name === 'survey-view') {
                    this.$router.push({ name: 'surveys-index' });
                }
            }

            this.$emit('close');
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

#print-quiz .modal-card-body {
    th {
        display: none;
    }
}

/* Flex column: footer stays at bottom; questions scroll in .scrollable-container (not sticky/absolute). */
.quick-quiz-preview-root {
    display: flex;
    flex-direction: column;
    min-height: 0;
    width: 100%;
    /* Dialog content often has no explicit height — % height fails; use viewport so flex + overflow scroll work. */
    height: 85vh;
    max-height: 85vh;
}

.quick-quiz-preview-root__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.quick-quiz-preview-root__body > section {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.quick-quiz-preview-root__footer {
    flex-shrink: 0;
    position: static;
    width: 100%;
}

.modal-background-color {
    background-color: $white;
    color: $black;
    min-width: 70rem;

    .setting-titles {
        color: $black;
    }
}

.quick-quiz-preview-root--tablet {
    min-width: unset;
    width: 100%;
    max-width: min(95vw, 56rem);
    margin: 0 auto;
}

/* Compound selectors beat global .modal-card-head / .modal-card-title
   (justify-content: flex-end, flex-shrink: 0) when source order loses the tie. */
.modal-card-head.quick-quiz-preview-root__head {
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
}

.modal-card-title.quick-quiz-preview-root__title {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    flex-shrink: 1;
    gap: 0.5rem;
    min-width: 0;
    overflow: hidden;
}

.quick-quiz-preview-root__title-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.quick-quiz-preview-root__tags {
    flex-shrink: 0;
    flex-wrap: nowrap;
}

.quick-quiz-preview-root__close {
    flex-shrink: 0;
    margin-left: 0 !important;
}

.quick-quiz-preview-root--tablet .modal-card-head.p-t-lg {
    padding-top: 1rem;
}

.mobile-modal,
.quick-quiz-preview-mobile {
    background-color: $white;
    color: $black;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 85vh;
    min-height: 0;
    overflow: hidden;
}

.modal-card-head.quick-quiz-preview-mobile__head {
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
    padding: 0.75rem 1rem;
}

.modal-card-title.quick-quiz-preview-mobile__title {
    flex: 1 1 auto;
    flex-shrink: 1;
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.quick-quiz-preview-mobile__close {
    flex-shrink: 0;
    margin-left: 0 !important;
}

.quick-quiz-preview-mobile__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 0.75rem;
}

.quick-quiz-preview-mobile__body .modal-card-body {
    padding: 0.5rem 0;
}

.quick-quiz-preview-mobile__body .card {
    margin-bottom: 0.5rem;
}

.quick-quiz-preview-mobile__foot {
    flex-shrink: 0;
    justify-content: flex-end;
    padding: 0.75rem 1rem;
}

/* Expand the hosting dialog on tablet so the preview uses available viewport width. */
@media (min-width: 769px) and (max-width: 1199px) {
    .p-dialog:has(.quick-quiz-preview-root),
    .p-dialog:has(.quick-quiz-preview-mobile) {
        width: min(95vw, 56rem) !important;
        max-width: min(95vw, 56rem) !important;
    }
}

.display-content-custom {
    max-width: 100% !important;
    text-overflow: clip !important;
    white-space: pre-wrap !important;
    margin-bottom: unset !important;
}

.scrollable-container {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

.quick-quiz-preview-questions {
    width: 100%;
}

.quick-quiz-preview-question {
    padding: 0.75rem 0.5rem;
}

.quick-quiz-preview-question--striped {
    background-color: var(--p-datatable-row-striped-background, rgba(0, 0, 0, 0.04));
}

</style>
